/**
 * PicklistOrderSection — per-location picklist sort order (BLU-502).
 *
 * Ordered sort keys with a combined mode+direction "Order" control, custom
 * value sequences, PER-PARENT custom sequences (colours running differently
 * inside each product type), and an optional section-heading toggle. Stored at
 * `locations.production_settings.picklist_sort`; the batch picklist, the A4
 * print sheet and the reversed thermal label stack all follow it in lockstep
 * (one comparator, in @threadheads/shared).
 *
 * Console dirty-model section (useDirtyRegistration), bound to the header
 * LocationFilter selection. Pending edits are dropped when the header location
 * changes (PickingSettingsSection idiom). Shared locations render READ-ONLY as
 * static text rather than disabled controls — the order is owned by the
 * fulfilment location's owner org (the backend enforces this with a FORBIDDEN),
 * but a client-org operator should still be able to READ it.
 *
 * ⚠ The body only mounts once the config query has settled. Rendering controls
 * over the platform default while the real config was in flight let an early
 * edit produce a draft derived from the DEFAULT, which Save then wrote over the
 * location's real order.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertCircle,
  ArrowDownWideNarrow,
  ChevronDown,
  ChevronUp,
  CircleSlash,
  Database,
  FlaskConical,
  GripVertical,
  Layers,
  Loader2,
  Lock,
  MapPin,
  Plus,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { EmptyState } from '@/components/shared/EmptyState';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useFilterOptions } from '@/features/production-orders/hooks/useOrderQueue';
import { useReorderableList } from '@/hooks/useReorderableList';
import {
  DEFAULT_PICKLIST_SORT,
  PICKLIST_SORT_MAX_KEYS,
  PICKLIST_SORT_MAX_ORDER_VALUES,
  PICKLIST_SORT_MAX_SCOPED_GROUPS,
  SIZE_ORDER,
  SORT_FIELD_META,
  buildPicklistComparator,
  getSectionKey,
  getSectionLabel,
  normalizeSize,
  normalizeTextKey,
  type PicklistSortConfig,
  type PicklistSortField,
  type PicklistSortKey,
  type PicklistSortable,
  type SortDirection,
  type SortMode,
} from '@threadheads/shared';
import { SettingsSection } from '../components/console/SettingsSection';
import { SettingRow, SettingRowDivider } from '../components/console/SettingRow';
import { useDirtyRegistration } from '../components/console/SettingsDirtyContext';

const ALL_FIELDS: PicklistSortField[] = ['productType', 'color', 'size', 'sku', 'productName'];

/**
 * Preview line cap, and the size of the synthetic grid built beneath it.
 *
 * Sized so the grid plus its two edge rows (one NULL-colour POD, one P&P) fits
 * even in the worst case, where a size-first sort means no two consecutive rows
 * share a (type, colour) pair and nothing aggregates: 3×3×3 = 27 rows → at most
 * 27 lines + 2 edge = 29. Those edge rows exist to demonstrate the empty-value
 * and locked-tier behaviour, so truncating them away defeats the point.
 * Truncation is still possible on a real queue, and is stated when it happens.
 */
const MAX_PREVIEW_LINES = 30;
const PREVIEW_GRID_TYPES = 3;
const PREVIEW_GRID_COLORS = 3;
const PREVIEW_GRID_SIZES = 3;

/**
 * Shared grid template for the tier-0 row and every key row, so the Order
 * control and the action cluster land at the same x on EVERY row regardless of
 * which controls that field has.
 *
 * Wide:   [ordinal] [label] [order] [actions]
 * Narrow: [ordinal] [label .......]
 *         [order .......] [actions]
 * DOM order is ordinal, label, order, actions, detail — so tab order matches
 * visual order at both breakpoints.
 */
const KEY_ROW_GRID = cn(
  'grid items-center gap-x-3 gap-y-2 px-4 py-3',
  'grid-cols-[1.75rem_minmax(0,1fr)_auto]',
  'sm:grid-cols-[1.75rem_minmax(7rem,1fr)_12rem_auto]',
);
const CELL_ORDINAL =
  'col-start-1 row-start-1 text-sm font-semibold tabular-nums text-muted-foreground';
const CELL_LABEL = 'col-start-2 col-span-2 row-start-1 sm:col-span-1';
const CELL_ORDER = 'col-start-2 row-start-2 h-9 w-full sm:col-start-3 sm:row-start-1';
const CELL_ACTIONS =
  'col-start-3 row-start-2 flex items-center gap-0.5 justify-self-end sm:col-start-4 sm:row-start-1';
const CELL_DETAIL = 'col-start-2 col-span-2 row-start-3 min-w-0 sm:col-span-3 sm:row-start-2';

const NORMALISED_PREFIX = 'Normalised: ';

function cloneConfig(c: PicklistSortConfig): PicklistSortConfig {
  return {
    fulfillmentDir: c.fulfillmentDir,
    keys: c.keys.map((k) => ({
      ...k,
      order: k.order ? [...k.order] : undefined,
      orderByScope: k.orderByScope
        ? Object.fromEntries(Object.entries(k.orderByScope).map(([s, v]) => [s, [...v]]))
        : undefined,
    })),
    bandByFirstKey: c.bandByFirstKey,
  };
}

function configEquals(a: PicklistSortConfig, b: PicklistSortConfig): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Combined mode+direction value for the single "Order" select. A pure
 * presentation collapse over the stored `{ mode, dir }` — nothing new is
 * persisted, and it removes the two problems the split controls had: the
 * direction select landed at a different x on every row (because the mode
 * select only exists for some fields), and it was REPLACED by a text span in
 * custom mode, so every control shifted when the mode changed.
 */
function orderValueOf(key: PicklistSortKey): string {
  if (key.mode === 'custom') return 'custom';
  return `${key.mode ?? 'default'}:${key.dir}`;
}

function orderOptions(field: PicklistSortField): { value: string; label: string }[] {
  const meta = SORT_FIELD_META[field];
  const modes: (SortMode | undefined)[] = meta.modes.length > 0 ? [...meta.modes] : [undefined];
  const out: { value: string; label: string }[] = [];
  for (const mode of modes) {
    if (mode === 'custom') continue;
    const copy = meta.directionCopy(mode);
    const prefix = mode === 'standard-normalized' ? NORMALISED_PREFIX : '';
    out.push({ value: `${mode ?? 'default'}:asc`, label: `${prefix}${copy.asc}` });
    out.push({ value: `${mode ?? 'default'}:desc`, label: `${prefix}${copy.desc}` });
  }
  if (meta.modes.includes('custom')) out.push({ value: 'custom', label: 'Custom order' });
  return out;
}

/** Read-only rendering of one key's order. */
function orderSummary(key: PicklistSortKey): string {
  if (key.mode === 'custom') {
    const listed = key.order ?? [];
    const scopeCount = Object.keys(key.orderByScope ?? {}).length;
    const base = listed.length > 0 ? `Custom order: ${listed.join(', ')}` : 'Custom order';
    return key.scopedBy && scopeCount > 0
      ? `${base} (plus ${scopeCount} per ${SORT_FIELD_META[key.scopedBy].label})`
      : base;
  }
  const meta = SORT_FIELD_META[key.field];
  const copy = meta.directionCopy(key.mode);
  const prefix = key.mode === 'standard-normalized' ? NORMALISED_PREFIX : '';
  return `${prefix}${key.dir === 'desc' ? copy.desc : copy.asc}`;
}

// ---------------------------------------------------------------------------
// Preview
// ---------------------------------------------------------------------------

/** Fixture preview rows for a drained queue / restricted viewer. */
const FIXTURE_ROWS: PicklistSortable[] = [
  { sku: 'TEE-BK', size: 'S', color: 'Black', shopifyProductType: 'Classic Tee', productName: 'Classic Tee', fulfillmentType: 'POD' },
  { sku: 'TEE-BK', size: 'M', color: 'Black', shopifyProductType: 'Classic Tee', productName: 'Classic Tee', fulfillmentType: 'POD' },
  { sku: 'TEE-CH', size: 'S', color: 'Charcoal', shopifyProductType: 'Classic Tee', productName: 'Classic Tee', fulfillmentType: 'POD' },
  { sku: 'OTE-BK', size: 'M', color: 'Black', shopifyProductType: 'Oversized Tee', productName: 'Oversized Tee', fulfillmentType: 'POD' },
  { sku: 'OTE-CH', size: 'M', color: 'Charcoal', shopifyProductType: 'Oversized Tee', productName: 'Oversized Tee', fulfillmentType: 'POD' },
  { sku: 'HD-BK', size: 'L', color: 'Black', shopifyProductType: 'Hoodie', productName: 'Heavy Hoodie', fulfillmentType: 'POD' },
  { sku: 'TB-NT', size: 'OS', color: null, shopifyProductType: 'Tote Bag', productName: 'Tote', fulfillmentType: 'POD' },
  { sku: 'CARD-A6', size: 'OS', color: null, shopifyProductType: null, productName: 'Greeting Card', fulfillmentType: 'P&P' },
];

type PreviewRow =
  | { kind: 'tier'; key: string; label: string }
  | { kind: 'band'; key: string; label: string }
  | { kind: 'line'; key: string; type: string; color: string; sizes: string[]; banded: boolean };

interface PreviewUnit {
  tier: string;
  sectionKey: string;
  sectionLabel: string;
  type: string;
  color: string;
  sizes: string[];
}

/**
 * Aggregate sorted rows into preview lines, then insert the locked fulfilment
 * tier headings and (when enabled) the first-key band headings.
 *
 * Tier rows are emitted ALWAYS — the tier is structural and locked, and it is
 * the only thing that makes the `fulfillmentDir` control legible. Band rows
 * follow the sheet's composite rule: a band restarts at a tier boundary even
 * when the value is unchanged, so the P&P block always opens with its own
 * heading.
 */
function buildPreviewRows(
  rows: PicklistSortable[],
  config: PicklistSortConfig,
): { rows: PreviewRow[]; truncated: boolean } {
  const banded = config.bandByFirstKey && config.keys.length > 0;
  const sorted = [...rows].sort(buildPicklistComparator(config));

  const units: PreviewUnit[] = [];
  for (const row of sorted) {
    const tier = row.fulfillmentType === 'P&P' ? 'P&P' : 'POD';
    const sectionKey = banded ? getSectionKey(config, row) : '';
    const sectionLabel = banded ? getSectionLabel(config, row) || 'Unspecified' : '';
    const type =
      (tier === 'P&P' ? row.productName : row.shopifyProductType) || row.productName || row.sku;
    const color = row.color || '';
    const last = units[units.length - 1];
    if (
      last &&
      last.tier === tier &&
      last.sectionKey === sectionKey &&
      last.type === type &&
      last.color === color
    ) {
      if (!last.sizes.includes(row.size)) last.sizes.push(row.size);
    } else {
      units.push({ tier, sectionKey, sectionLabel, type, color, sizes: [row.size] });
    }
  }

  const truncated = units.length > MAX_PREVIEW_LINES;
  const out: PreviewRow[] = [];
  let lastTier: string | null = null;
  let lastSection: string | null = null;
  units.slice(0, MAX_PREVIEW_LINES).forEach((unit, i) => {
    if (unit.tier !== lastTier) {
      out.push({
        kind: 'tier',
        key: `tier-${i}`,
        label: unit.tier === 'P&P' ? 'Pick and pack' : 'Print on demand',
      });
      lastTier = unit.tier;
      lastSection = null;
    }
    if (banded && unit.sectionKey !== lastSection) {
      out.push({ kind: 'band', key: `band-${i}`, label: unit.sectionLabel });
      lastSection = unit.sectionKey;
    }
    out.push({
      kind: 'line',
      key: `line-${i}`,
      type: unit.type,
      color: unit.color,
      sizes: unit.sizes,
      banded,
    });
  });

  return { rows: out, truncated };
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export function PicklistOrderSection() {
  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();
  const locationId = selectedLocation?.id;

  // Shared-location read-only: picklist order belongs to the owner org.
  // owner_organization_id is NULL on legacy UI-created locations → treat as
  // owned (matches the backend's ?? organization_id fallback).
  const isOwned =
    !!selectedLocation &&
    (!selectedLocation.owner_organization_id ||
      selectedLocation.owner_organization_id === organization?.id);
  const readOnly = !selectedLocation || !isOwned;

  const configQuery = trpc.productionSettings.getPicklistSortConfig.useQuery(
    { locationId: locationId ?? '' },
    { enabled: !!locationId },
  );
  const setConfig = trpc.productionSettings.setPicklistSortConfig.useMutation({
    // SettingsDirtyContext assumes the section toasts its own message; the save
    // bar only reports which sections failed. Without this the specific
    // FORBIDDEN ("Picklist order is set by the location owner") is swallowed.
    onError: (error) => toast.error(`Could not save the picklist order. ${error.message}`),
  });
  const utils = trpc.useUtils();
  const { data: aliasData } = trpc.productionSettings.getProductTypeAliases.useQuery();
  const aliases = aliasData?.aliases;

  // Real value space for the custom pickers + live preview. getFilterOptions is
  // the spine because it is the only source that is BOTH location-scoped and
  // cross-org resolved — the two properties that decide correctness at a 3PL
  // shared location. Scoped to NEW/unbatched orders, so empty is normal for a
  // drained queue (fixture fallback below, never an empty preview).
  const filterOptions = useFilterOptions(locationId);
  const queueColors = useMemo(() => filterOptions.data?.colors ?? [], [filterOptions.data?.colors]);
  const queueColorKeys = useMemo(
    () => new Set(queueColors.map((c) => normalizeTextKey(c))),
    [queueColors],
  );

  /**
   * Product-type vocabulary, ALIAS-PROJECTED. The comparator only ever sees the
   * displayed value (BatchDetailScreen projects `aliases[raw] ?? raw`), so the
   * stored lists must hold display names, never raw Shopify types.
   *
   * Seeded with the org's alias display values so a drained queue still offers
   * the curated names, and so a renamed alias shows up as an option the moment
   * it is renamed. Queue values come FIRST, so the surviving spelling is the
   * one actually flowing through this location.
   */
  const queueProductTypes = useMemo(() => {
    const raw = filterOptions.data?.productTypes ?? [];
    const out: string[] = [];
    const seen = new Set<string>();
    for (const value of [...raw.map((t) => aliases?.[t] ?? t), ...Object.values(aliases ?? {})]) {
      const k = normalizeTextKey(value);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push(value);
    }
    return out.sort((a, b) => a.localeCompare(b));
  }, [filterOptions.data?.productTypes, aliases]);
  const queueProductTypeKeys = useMemo(
    () => new Set(queueProductTypes.map((t) => normalizeTextKey(t))),
    [queueProductTypes],
  );

  const saved = useMemo(
    () => configQuery.data ?? cloneConfig(DEFAULT_PICKLIST_SORT),
    [configQuery.data],
  );
  const [pending, setPending] = useState<PicklistSortConfig | null>(null);
  const config = pending ?? saved;
  const isDirty = pending !== null && !configEquals(pending, saved);
  const isDefaultConfig = configEquals(config, DEFAULT_PICKLIST_SORT);

  const keyList = useReorderableList(config.keys);

  // Header-location change drops pending edits — they no longer target
  // anything real (PickingSettingsSection idiom).
  const locationIdRef = useRef(locationId);
  useEffect(() => {
    if (locationIdRef.current === locationId) return;
    locationIdRef.current = locationId;
    setPending(null);
  }, [locationId]);

  // A failed refetch must not leave a draft built on data we no longer trust.
  useEffect(() => {
    if (configQuery.isError) setPending(null);
  }, [configQuery.isError]);

  useDirtyRegistration(
    'picklist-order',
    isDirty && locationId
      ? {
          label: 'Picklist order',
          save: async () => {
            // Saving the exact default clears the key instead — the location
            // stays "unset" and keeps tracking the platform default.
            const value = pending && !configEquals(pending, DEFAULT_PICKLIST_SORT) ? pending : null;
            await setConfig.mutateAsync({ locationId, config: value });
            await utils.productionSettings.getPicklistSortConfig.invalidate({ locationId });
            setPending(null);
          },
          discard: () => setPending(null),
        }
      : null,
  );

  const edit = useCallback(
    (mutate: (draft: PicklistSortConfig) => void) => {
      setPending((prev) => {
        const draft = cloneConfig(prev ?? saved);
        mutate(draft);
        return draft;
      });
    },
    [saved],
  );

  const moveKey = (index: number, delta: -1 | 1) => {
    const key = config.keys[index];
    if (!key) return;
    const label = SORT_FIELD_META[key.field].label;
    const target = index + delta;
    if (target < 0 || target >= config.keys.length) {
      // The chevron stays enabled so focus never drops to <body>; a silent
      // no-op would leave a screen-reader user with no feedback at all.
      keyList.announce(delta < 0 ? `${label} is already first.` : `${label} is already last.`);
      return;
    }

    // ⚠ Moving a scoped key above its scope key invalidates the per-parent
    // orders (the sanitizer drops them on the next read). Never silently.
    const swapped = config.keys[target];
    const scopedKey =
      key.scopedBy && swapped?.field === key.scopedBy && delta === -1
        ? key
        : swapped?.scopedBy === key.field && delta === 1
          ? swapped
          : null;
    if (scopedKey?.scopedBy) {
      const scopeLabel = SORT_FIELD_META[scopedKey.scopedBy].label;
      const groups = Object.keys(scopedKey.orderByScope ?? {}).length;
      if (groups > 0) {
        const confirmed = window.confirm(
          `${SORT_FIELD_META[scopedKey.field].label} is ordered per ${scopeLabel}. ` +
            `Moving it above ${scopeLabel} discards ${groups} per-${scopeLabel.toLowerCase()} ` +
            `order${groups === 1 ? '' : 's'}.`,
        );
        if (!confirmed) return;
      }
      edit((draft) => {
        const stale = draft.keys.find((k) => k.field === scopedKey.field);
        if (stale) {
          delete stale.scopedBy;
          delete stale.orderByScope;
        }
      });
    }

    edit((draft) => {
      const [moving] = draft.keys.splice(index, 1);
      if (moving) draft.keys.splice(target, 0, moving);
    });
    keyList.announce(`${label} moved to position ${target + 1}.`);
  };

  const setOrderValue = (index: number, value: string) => {
    edit((draft) => {
      const key = draft.keys[index];
      if (!key) return;
      if (value === 'custom') {
        key.mode = 'custom';
        key.dir = 'asc'; // the list IS the order
        key.order = key.order ?? [];
        return;
      }
      const [mode, dir] = value.split(':');
      if (mode === 'default') delete key.mode;
      else key.mode = mode as SortMode;
      key.dir = (dir as SortDirection) ?? 'asc';
      delete key.order;
      delete key.scopedBy;
      delete key.orderByScope;
    });
  };

  const addKey = (field: PicklistSortField) => {
    edit((draft) => {
      const meta = SORT_FIELD_META[field];
      const key: PicklistSortKey = { field, dir: 'asc' };
      if (meta.modes.length > 0) key.mode = meta.modes[0];
      draft.keys.push(key);
    });
    keyList.announce(`${SORT_FIELD_META[field].label} added to the sort order.`);
  };

  const removeKey = (index: number) => {
    const removed = config.keys[index];
    if (!removed) return;
    const successor = config.keys[index + 1]?.field ?? config.keys[index - 1]?.field ?? null;
    edit((draft) => {
      draft.keys.splice(index, 1);
      // Any key scoped BY the removed field would be dropped by the sanitizer
      // on the next read; do it here so the UI never shows an order that will
      // not survive a reload.
      for (const key of draft.keys) {
        if (key.scopedBy === removed.field) {
          delete key.scopedBy;
          delete key.orderByScope;
        }
      }
    });
    keyList.focusAfter(successor);
    keyList.announce(`${SORT_FIELD_META[removed.field].label} removed from the sort order.`);
  };

  const resetToDefault = () => {
    setPending(cloneConfig(DEFAULT_PICKLIST_SORT));
    keyList.announce('Sort keys reset to the default order.');
  };

  const availableFields = ALL_FIELDS.filter((f) => !config.keys.some((k) => k.field === f));
  const canAdd = availableFields.length > 0 && config.keys.length < PICKLIST_SORT_MAX_KEYS;

  /** One vocabulary per field, feeding BOTH its value list and any scope-group picker. */
  const vocabularyFor = useCallback(
    (field: PicklistSortField): Vocabulary => {
      if (field === 'size') {
        return {
          options: [...SIZE_ORDER] as string[],
          normalize: normalizeSize,
          // Closed vocabulary, so an "unknown value" marker would be noise.
          knownValueKeys: null,
          unlistedNote: 'Sizes you do not list follow the standard size order after these.',
          searchLabel: 'sizes',
        };
      }
      if (field === 'productType') {
        return {
          options: queueProductTypes,
          normalize: normalizeTextKey,
          knownValueKeys: queueProductTypeKeys,
          unlistedNote:
            'Product types you do not list follow in alphabetical order after these. Names must match the Type / Product column, so if you rename a product type alias, update this list too.',
          searchLabel: 'product types',
        };
      }
      return {
        options: queueColors,
        normalize: normalizeTextKey,
        knownValueKeys: queueColorKeys,
        unlistedNote: 'Colours you do not list follow in alphabetical order after these.',
        searchLabel: 'colours',
      };
    },
    [queueColors, queueColorKeys, queueProductTypes, queueProductTypeKeys],
  );

  // Live preview from the location's REAL value space (alias-projected, the
  // same projection the batch screen applies), plus two synthetic edge rows:
  // one NULL-colour POD and one P&P. Fixture when the queue is drained or the
  // endpoint is unavailable.
  const { previewRows, previewTruncated, previewIsFixture } = useMemo(() => {
    const opts = filterOptions.data;
    if (!opts || opts.productTypes.length === 0) {
      const built = buildPreviewRows(FIXTURE_ROWS, config);
      return { previewRows: built.rows, previewTruncated: built.truncated, previewIsFixture: true };
    }
    const types = opts.productTypes.slice(0, PREVIEW_GRID_TYPES);
    const colors = opts.colors.slice(0, PREVIEW_GRID_COLORS);
    const sizes = (opts.sizes.length > 0 ? opts.sizes : ['S', 'M', 'L']).slice(0, PREVIEW_GRID_SIZES);
    const rows: PicklistSortable[] = [];
    outer: for (const type of types) {
      for (const color of colors.length > 0 ? colors : ['']) {
        for (const size of sizes) {
          if (rows.length >= PREVIEW_GRID_TYPES * PREVIEW_GRID_COLORS * PREVIEW_GRID_SIZES)
            break outer;
          const displayType = aliases?.[type] ?? type;
          rows.push({
            sku: `${type.replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase() || 'SKU'}-${color.replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || 'XX'}`,
            size,
            color: color || null,
            shopifyProductType: displayType,
            productName: displayType,
            fulfillmentType: 'POD',
          });
        }
      }
    }
    const firstType = types[0] ?? 'Tee';
    rows.push({
      sku: 'NOCOL-1',
      size: sizes[0] ?? 'M',
      color: null,
      shopifyProductType: aliases?.[firstType] ?? firstType,
      productName: aliases?.[firstType] ?? firstType,
      fulfillmentType: 'POD',
    });
    rows.push({
      sku: 'CARD-A6',
      size: 'OS',
      color: null,
      shopifyProductType: null,
      productName: 'Greeting Card',
      fulfillmentType: 'P&P',
    });
    const built = buildPreviewRows(rows, config);
    return { previewRows: built.rows, previewTruncated: built.truncated, previewIsFixture: false };
  }, [filterOptions.data, aliases, config]);

  const firstKeyLabel = config.keys[0] ? SORT_FIELD_META[config.keys[0].field].label : null;

  // -------------------------------------------------------------------------
  // States: empty → loading → error → (read-only | editable)
  // -------------------------------------------------------------------------

  let body: React.ReactNode;

  if (!selectedLocation) {
    body = (
      <EmptyState
        size="sm"
        icon={MapPin}
        title="No location selected"
        description="Choose a location in the header to set its picklist order."
      />
    );
  } else if (configQuery.isLoading) {
    body = (
      <div aria-busy="true" className="space-y-4">
        <span className="sr-only">Loading picklist order</span>
        <div className="-mx-4 divide-y divide-border/70 border-y border-border/70">
          {[0, 1, 2].map((i) => (
            <div key={i} className={KEY_ROW_GRID}>
              <Skeleton className="col-start-1 row-start-1 h-4 w-5" />
              <Skeleton className="col-start-2 col-span-2 row-start-1 h-4 w-28 sm:col-span-1" />
              <Skeleton className="col-start-2 row-start-2 h-9 w-full sm:col-start-3 sm:row-start-1" />
              <Skeleton className="col-start-3 row-start-2 h-8 w-24 justify-self-end sm:col-start-4 sm:row-start-1" />
            </div>
          ))}
        </div>
        <Skeleton className="h-9 w-36" />
      </div>
    );
  } else if (configQuery.isError) {
    body = (
      <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-medium">Could not load the picklist order</p>
            <p className="text-xs text-muted-foreground">
              Nothing has changed. Picking is still using this location&apos;s saved order.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="mt-3 h-9 gap-1.5"
          onClick={() => void configQuery.refetch()}
          disabled={configQuery.isFetching}
        >
          {configQuery.isFetching ? (
            <Loader2 className="animate-spin" aria-hidden="true" />
          ) : null}
          Try again
        </Button>
      </div>
    );
  } else if (readOnly) {
    // Static, readable, full contrast. NOT `inert` and NOT a wall of disabled
    // controls: this content is permanently relevant, so a client-org operator
    // must still be able to read the owner's configured order.
    body = (
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-md border bg-muted/50 px-3 py-2 text-xs">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p>
            This location&apos;s owner sets the picklist order, so it cannot be changed here. The
            current order is shown below.
          </p>
        </div>
        <ol className="-mx-4 divide-y divide-border/70 border-y border-border/70">
          <li className={KEY_ROW_GRID}>
            <span className={CELL_ORDINAL}>0.</span>
            <span className={cn(CELL_LABEL, 'text-sm font-medium')}>Fulfilment type</span>
            <span className="col-start-2 row-start-2 text-sm text-muted-foreground sm:col-start-3 sm:row-start-1">
              {config.fulfillmentDir === 'desc' ? 'P&P first, then POD' : 'POD first, then P&P'}
            </span>
          </li>
          {config.keys.map((key, index) => (
            <li key={key.field} className={KEY_ROW_GRID}>
              <span className={CELL_ORDINAL}>{index + 1}.</span>
              <span className={cn(CELL_LABEL, 'text-sm font-medium')}>
                {SORT_FIELD_META[key.field].label}
              </span>
              <span className="col-start-2 row-start-2 text-sm text-muted-foreground sm:col-start-3 sm:row-start-1">
                {orderSummary(key)}
              </span>
            </li>
          ))}
        </ol>
      </div>
    );
  } else {
    body = (
      <div className="space-y-4">
        <p id="picklist-order-help" className="text-xs text-muted-foreground">
          Rows are compared from the top down. The first row decides the main grouping.
        </p>

        <ol
          className="-mx-4 divide-y divide-border/70 border-y border-border/70"
          aria-label="Sort order, highest priority first"
          aria-describedby="picklist-order-help"
        >
          {/* Locked tier 0 */}
          <li className={KEY_ROW_GRID}>
            <span className={CELL_ORDINAL}>0.</span>
            <div className={cn(CELL_LABEL, 'flex min-w-0 flex-wrap items-center gap-2')}>
              <span className="text-sm font-medium">Fulfilment type</span>
              <span className="inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground">
                <Lock className="h-3 w-3" aria-hidden="true" />
                Locked
              </span>
            </div>
            <Select
              value={config.fulfillmentDir}
              onValueChange={(value: SortDirection) =>
                edit((draft) => {
                  draft.fulfillmentDir = value;
                })
              }
            >
              <SelectTrigger className={CELL_ORDER} aria-label="Fulfilment tier order">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asc">POD first, then P&amp;P</SelectItem>
                <SelectItem value="desc">P&amp;P first, then POD</SelectItem>
              </SelectContent>
            </Select>
            {/* Reserves the actions column so every row's control ends at the same x. */}
            <div aria-hidden className={CELL_ACTIONS} />
            <p className={cn(CELL_DETAIL, 'text-xs text-muted-foreground')}>
              P&amp;P items are picked separately, so they always form their own block. You can only
              choose which block comes first.
            </p>
          </li>

          {config.keys.length === 0 ? (
            <li className="px-4 py-6">
              <p className="text-sm text-muted-foreground">
                No sort keys. Rows are grouped by fulfilment type only, in the order they arrive.
              </p>
            </li>
          ) : null}

          {config.keys.map((key, index) => {
            const meta = SORT_FIELD_META[key.field];
            const isCustom = key.mode === 'custom';
            return (
              <li key={key.field} className={KEY_ROW_GRID}>
                <span className={CELL_ORDINAL}>{index + 1}.</span>
                <span className={cn(CELL_LABEL, 'truncate text-sm font-medium')}>{meta.label}</span>

                <Select
                  value={orderValueOf(key)}
                  onValueChange={(value) => setOrderValue(index, value)}
                >
                  <SelectTrigger className={CELL_ORDER} aria-label={`${meta.label} order`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {orderOptions(key.field).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className={CELL_ACTIONS}>
                  {/* Chevrons stay ENABLED at the ends (they announce instead)
                      so keyboard focus never drops to <body> mid-reorder. */}
                  <EndAwareIconButton
                    atEnd={index === 0}
                    onClick={() => moveKey(index, -1)}
                    label={`Move ${meta.label} up in the sort order`}
                    icon={ChevronUp}
                  />
                  <EndAwareIconButton
                    atEnd={index === config.keys.length - 1}
                    onClick={() => moveKey(index, 1)}
                    label={`Move ${meta.label} down in the sort order`}
                    icon={ChevronDown}
                  />
                  <Button
                    ref={keyList.registerRef(key.field)}
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => removeKey(index)}
                    aria-label={`Remove ${meta.label} from the sort order`}
                  >
                    <X aria-hidden="true" />
                  </Button>
                </div>

                {key.mode === 'standard-normalized' || isCustom ? (
                  <div className={cn(CELL_DETAIL, 'space-y-2')}>
                    {key.mode === 'standard-normalized' ? (
                      <p className="text-xs text-muted-foreground">
                        Treats Large as L and XXL as 2XL before sorting.
                      </p>
                    ) : null}

                    {isCustom ? (
                      <CustomOrderEditor
                        keyIndex={index}
                        sortKey={key}
                        earlierKeys={config.keys.slice(0, index)}
                        vocab={vocabularyFor(key.field)}
                        scopeVocabulary={key.scopedBy ? vocabularyFor(key.scopedBy) : null}
                        scopeGroupOrder={
                          key.scopedBy
                            ? (config.keys.find((k) => k.field === key.scopedBy)?.order ?? [])
                            : []
                        }
                        edit={edit}
                      />
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>

        <div className="flex flex-wrap items-center gap-2">
          {canAdd ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button ref={keyList.addRef} variant="outline" size="sm" className="h-9 gap-1.5">
                  <Plus aria-hidden="true" />
                  Add sort key
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[200px]">
                {availableFields.map((field) => (
                  <DropdownMenuItem key={field} onSelect={() => addKey(field)}>
                    {SORT_FIELD_META[field].label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <p className="text-xs text-muted-foreground">All sort keys are in use.</p>
          )}

          {!isDefaultConfig ? (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-9 gap-1.5 text-muted-foreground"
              onClick={resetToDefault}
            >
              <RotateCcw aria-hidden="true" />
              Reset to default order
            </Button>
          ) : null}
        </div>

        {!isDefaultConfig ? (
          <p className="text-xs text-muted-foreground">The default order is size, then SKU.</p>
        ) : null}

        <SettingRowDivider />
        <SettingRow
          htmlFor="picklist-band-by-first-key"
          label="Break the list into sections"
          description={
            firstKeyLabel
              ? `Adds a heading row each time ${firstKeyLabel} changes, on screen and on the printed sheet.`
              : 'Add at least one sort key to use section headings.'
          }
        >
          <Switch
            id="picklist-band-by-first-key"
            checked={config.bandByFirstKey}
            onCheckedChange={(checked) =>
              edit((draft) => {
                draft.bandByFirstKey = checked === true;
              })
            }
            disabled={config.keys.length === 0}
          />
        </SettingRow>
      </div>
    );
  }

  return (
    <SettingsSection
      id="picklist-order"
      title="Picklist order"
      description="The order pickers walk the bays, on screen and on the printed sheet."
      icon={ArrowDownWideNarrow}
      scope="location"
      headerRight={
        setConfig.isPending ? (
          <span
            role="status"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Saving
          </span>
        ) : setConfig.isError ? (
          <span role="status" className="inline-flex items-center gap-1.5 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
            Not saved
          </span>
        ) : null
      }
    >
      <div className="space-y-4">
        <span aria-live="polite" role="status" className="sr-only">
          {keyList.liveText}
        </span>

        {body}

        {selectedLocation && !configQuery.isLoading && !configQuery.isError ? (
          <div className="rounded-lg border border-dashed bg-muted/30 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold">Preview</h4>
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground">
                {previewIsFixture ? (
                  <>
                    <FlaskConical className="h-3 w-3" aria-hidden="true" />
                    Sample data
                  </>
                ) : (
                  <>
                    <Database className="h-3 w-3" aria-hidden="true" />
                    {selectedLocation.name} queue
                  </>
                )}
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {previewIsFixture
                ? 'No orders are waiting at this location right now, so this preview uses example rows.'
                : 'Built from the items currently waiting at this location.'}
            </p>

            {filterOptions.isLoading && !filterOptions.data ? (
              <div className="mt-3 space-y-1.5">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-5 w-full" />
                ))}
              </div>
            ) : (
              <ul className="mt-3 divide-y divide-border/60 overflow-hidden rounded-md border bg-background">
                {previewRows.map((line) =>
                  line.kind === 'tier' ? (
                    <li
                      key={line.key}
                      className="flex items-center gap-2 bg-muted px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-foreground"
                    >
                      <Layers className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                      {line.label}
                      <span className="ml-auto text-[10px] font-normal normal-case tracking-normal text-muted-foreground">
                        Locked tier
                      </span>
                    </li>
                  ) : line.kind === 'band' ? (
                    <li
                      key={line.key}
                      className="bg-muted/50 px-3 py-1 text-xs font-semibold text-foreground"
                    >
                      {line.label}
                    </li>
                  ) : (
                    <li
                      key={line.key}
                      className={cn(
                        'flex flex-wrap items-baseline gap-x-1.5 px-3 py-1 text-sm',
                        line.banded && 'pl-6',
                      )}
                    >
                      <span className="font-medium">{line.type}</span>
                      {line.color ? (
                        <>
                          <span aria-hidden className="text-muted-foreground">
                            ·
                          </span>
                          <span>{line.color}</span>
                        </>
                      ) : null}
                      <span aria-hidden className="text-muted-foreground">
                        ·
                      </span>
                      <span className="text-muted-foreground">{line.sizes.join(', ')}</span>
                    </li>
                  ),
                )}
                {previewTruncated ? (
                  <li className="px-3 py-1 text-xs text-muted-foreground">
                    Preview shortened. The printed sheet includes every row.
                  </li>
                ) : null}
              </ul>
            )}
          </div>
        ) : null}
      </div>
    </SettingsSection>
  );
}

/**
 * Ghost icon button that stays focusable at a list end instead of disabling
 * itself on activation. The old `opacity-40` measured 2.55:1 against white —
 * below the 3:1 floor for graphical objects — and was the only sighted signal
 * that the control no-ops.
 */
function EndAwareIconButton({
  atEnd,
  onClick,
  label,
  icon: Icon,
}: {
  atEnd: boolean;
  onClick: () => void;
  label: string;
  icon: typeof ChevronUp;
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        'h-8 w-8',
        atEnd
          ? 'cursor-default text-muted-foreground hover:bg-transparent hover:text-muted-foreground'
          : 'text-foreground',
      )}
      onClick={onClick}
      aria-disabled={atEnd}
      aria-label={label}
    >
      <Icon aria-hidden="true" />
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Custom order editor: the scope control + either one list or per-scope groups
// ---------------------------------------------------------------------------

interface Vocabulary {
  options: string[];
  normalize: (raw: string) => string;
  knownValueKeys: Set<string> | null;
  unlistedNote: string;
  searchLabel: string;
}

function CustomOrderEditor({
  keyIndex,
  sortKey,
  earlierKeys,
  vocab,
  scopeVocabulary,
  scopeGroupOrder,
  edit,
}: {
  keyIndex: number;
  sortKey: PicklistSortKey;
  earlierKeys: PicklistSortKey[];
  vocab: Vocabulary;
  scopeVocabulary: Vocabulary | null;
  /** The scope key's own custom order, so groups appear in bay-walk sequence. */
  scopeGroupOrder: string[];
  edit: (mutate: (draft: PicklistSortConfig) => void) => void;
}) {
  const label = SORT_FIELD_META[sortKey.field].label;
  const scopeLabel = sortKey.scopedBy ? SORT_FIELD_META[sortKey.scopedBy].label : null;
  const groups = useMemo(() => sortKey.orderByScope ?? {}, [sortKey.orderByScope]);
  const groupNames = useMemo(() => {
    const rank = new Map(scopeGroupOrder.map((v, i) => [normalizeTextKey(v), i]));
    return Object.keys(groups).sort((a, b) => {
      const ra = rank.get(normalizeTextKey(a)) ?? Number.MAX_SAFE_INTEGER;
      const rb = rank.get(normalizeTextKey(b)) ?? Number.MAX_SAFE_INTEGER;
      return ra !== rb ? ra - rb : a.localeCompare(b);
    });
  }, [groups, scopeGroupOrder]);

  const setScopedBy = (value: string) => {
    edit((draft) => {
      const key = draft.keys[keyIndex];
      if (!key) return;
      if (value === 'none') {
        delete key.scopedBy;
        delete key.orderByScope;
      } else {
        key.scopedBy = value as PicklistSortField;
        key.orderByScope = key.orderByScope ?? {};
      }
    });
  };

  const mutateList = (scope: string | null, mutate: (list: string[]) => string[]) => {
    edit((draft) => {
      const key = draft.keys[keyIndex];
      if (!key) return;
      if (scope === null) {
        key.order = mutate(key.order ?? []);
      } else {
        key.orderByScope = {
          ...(key.orderByScope ?? {}),
          [scope]: mutate(key.orderByScope?.[scope] ?? []),
        };
      }
    });
  };

  const scopeCandidates = earlierKeys.filter((k) => k.field !== sortKey.field);
  const usedScopeKeys = new Set(groupNames.map((n) => normalizeTextKey(n)));
  /**
   * Offer the scope key's OWN curated order first, then its queue vocabulary.
   * The operator has usually just typed the bay walk order into the parent key
   * ("Oversized Tee", "Hoodie"), and those are precisely the values they want
   * to give their own colour run — but they may not be in the current queue, so
   * the queue vocabulary alone would not list them.
   */
  const addableScopes = useMemo(() => {
    const out: string[] = [];
    const seen = new Set(usedScopeKeys);
    for (const option of [...scopeGroupOrder, ...(scopeVocabulary?.options ?? [])]) {
      const k = normalizeTextKey(option);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push(option);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeGroupOrder, scopeVocabulary?.options, groupNames.join(' ')]);

  return (
    <div className="space-y-2">
      {scopeCandidates.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">{label} order:</span>
          <Select value={sortKey.scopedBy ?? 'none'} onValueChange={setScopedBy}>
            <SelectTrigger className="h-8 w-[220px] text-xs" aria-label={`${label} order scope`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">The same everywhere</SelectItem>
              {scopeCandidates.map((k) => (
                <SelectItem key={k.field} value={k.field}>
                  Per {SORT_FIELD_META[k.field].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {!sortKey.scopedBy ? (
        <OrderedValueList
          listId={`key-${sortKey.field}`}
          fieldLabel={label}
          values={sortKey.order ?? []}
          vocab={vocab}
          onChange={(mutate) => mutateList(null, mutate)}
          note={vocab.unlistedNote}
        />
      ) : (
        <div className="space-y-2">
          <Accordion type="multiple" className="rounded-md border">
            <AccordionItem value="__default__" className="border-b last:border-b-0">
              <AccordionTrigger className="px-3 py-2 text-xs hover:no-underline">
                <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 text-left">
                  <span className="font-semibold">Default</span>
                  <span className="text-muted-foreground">
                    used by {scopeLabel?.toLowerCase()} values without their own order
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3">
                <OrderedValueList
                  listId={`key-${sortKey.field}-default`}
                  fieldLabel={label}
                  values={sortKey.order ?? []}
                  vocab={vocab}
                  onChange={(mutate) => mutateList(null, mutate)}
                  note={vocab.unlistedNote}
                />
              </AccordionContent>
            </AccordionItem>

            {groupNames.map((scope) => (
              <AccordionItem key={scope} value={scope} className="border-b last:border-b-0">
                <AccordionTrigger className="px-3 py-2 text-xs hover:no-underline">
                  <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 text-left">
                    <span className="truncate font-semibold">{scope}</span>
                    <span className="truncate text-muted-foreground">
                      {(groups[scope] ?? []).slice(0, 3).join(', ')}
                      {(groups[scope] ?? []).length > 3 ? ' …' : ''}
                    </span>
                    {scopeVocabulary?.knownValueKeys &&
                    !scopeVocabulary.knownValueKeys.has(normalizeTextKey(scope)) ? (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground">
                        <CircleSlash className="h-3 w-3" aria-hidden="true" />
                        Not in queue
                      </span>
                    ) : null}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-2 px-3 pb-3">
                  <OrderedValueList
                    listId={`key-${sortKey.field}-${normalizeTextKey(scope)}`}
                    fieldLabel={`${label} for ${scope}`}
                    values={groups[scope] ?? []}
                    vocab={vocab}
                    onChange={(mutate) => mutateList(scope, mutate)}
                    note={`This list replaces the default order for ${scope}. ${vocab.unlistedNote}`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1.5 text-muted-foreground"
                    onClick={() =>
                      edit((draft) => {
                        const key = draft.keys[keyIndex];
                        if (!key?.orderByScope) return;
                        const next = { ...key.orderByScope };
                        delete next[scope];
                        key.orderByScope = next;
                      })
                    }
                  >
                    <X aria-hidden="true" />
                    Remove this order
                  </Button>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {groupNames.length >= PICKLIST_SORT_MAX_SCOPED_GROUPS ? (
            <p className="text-xs text-muted-foreground">
              You have reached the limit of {PICKLIST_SORT_MAX_SCOPED_GROUPS} per-
              {scopeLabel?.toLowerCase()} orders.
            </p>
          ) : (
            <ScopeGroupPicker
              scopeLabel={scopeLabel ?? ''}
              options={addableScopes}
              onAdd={(scope) =>
                edit((draft) => {
                  const key = draft.keys[keyIndex];
                  if (!key) return;
                  // Seed from the default list so the operator edits a real
                  // starting point instead of an empty box.
                  key.orderByScope = {
                    ...(key.orderByScope ?? {}),
                    [scope]: [...(key.order ?? [])],
                  };
                })
              }
            />
          )}
        </div>
      )}
    </div>
  );
}

function ScopeGroupPicker({
  scopeLabel,
  options,
  onAdd,
}: {
  scopeLabel: string;
  options: string[];
  onAdd: (scope: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const filtered = query
    ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : options;
  const queryIsNew =
    query.trim().length > 0 &&
    !options.some((o) => normalizeTextKey(o) === normalizeTextKey(query.trim()));

  const add = (value: string) => {
    onAdd(value.trim());
    setQuery('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className="h-9 gap-1.5"
        >
          <Plus aria-hidden="true" />
          Give a {scopeLabel.toLowerCase()} its own order
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={`Search ${scopeLabel.toLowerCase()} values`}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {filtered.length === 0 && !queryIsNew ? (
              <CommandEmpty>No matches. Type to add a new value.</CommandEmpty>
            ) : (
              <CommandGroup>
                {filtered.map((option) => (
                  <CommandItem
                    key={option}
                    value={option}
                    onSelect={() => add(option)}
                    className="cursor-pointer"
                  >
                    {option}
                  </CommandItem>
                ))}
                {queryIsNew ? (
                  <CommandItem value={query} onSelect={() => add(query)} className="cursor-pointer">
                    Add &quot;{query.trim()}&quot;
                  </CommandItem>
                ) : null}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// One ordered value list: drag (dnd-kit) + chevrons + searchable add
// ---------------------------------------------------------------------------

/**
 * Vertical-axis constraint written inline: `@dnd-kit/modifiers` is not a
 * dependency and is not worth adding for six lines. Without it the row drifts
 * horizontally and gets clipped by SettingsSection's `overflow-hidden`.
 */
const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

function OrderedValueList({
  listId,
  fieldLabel,
  values,
  vocab,
  onChange,
  note,
}: {
  listId: string;
  fieldLabel: string;
  values: string[];
  vocab: Vocabulary;
  onChange: (mutate: (list: string[]) => string[]) => void;
  note: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const reducedMotion = useReducedMotion();
  const list = useReorderableList(values);
  const captionId = `${listId}-caption`;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // ⚠ HelpRecorderPage omits this; that is a bug there, not a pattern to
    // copy. Without it the grip is a mouse-only affordance.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Values are deduped on the normalized key by add(), so it is unique — and it
  // must NOT carry the index, or every row remounts on reorder and dnd-kit
  // loses node identity mid-drag.
  const ids = values.map((v) => vocab.normalize(v));

  const move = (index: number, delta: -1 | 1) => {
    const value = values[index];
    if (value === undefined) return;
    const target = index + delta;
    if (target < 0 || target >= values.length) {
      list.announce(delta < 0 ? `${value} is already first.` : `${value} is already last.`);
      return;
    }
    onChange((prev) => arrayMove(prev, index, target));
    list.announce(`${value} moved to position ${target + 1} in ${fieldLabel} order.`);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const moved = values[from];
    onChange((prev) => arrayMove(prev, from, to));
    list.announce(`${moved} moved to position ${to + 1} in ${fieldLabel} order.`);
  };

  const remove = (index: number) => {
    const removed = values[index];
    const successor = values[index + 1] ?? values[index - 1];
    onChange((prev) => prev.filter((_, i) => i !== index));
    list.focusAfter(successor !== undefined ? vocab.normalize(successor) : null);
    if (removed) list.announce(`${removed} removed from ${fieldLabel} order.`);
  };

  const listedKeys = new Set(ids);
  const available = vocab.options.filter((o) => !listedKeys.has(vocab.normalize(o)));
  const filtered = query
    ? available.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : available;
  const queryIsNew =
    query.trim().length > 0 &&
    !listedKeys.has(vocab.normalize(query.trim())) &&
    !available.some((o) => vocab.normalize(o) === vocab.normalize(query.trim()));
  const atCap = values.length >= PICKLIST_SORT_MAX_ORDER_VALUES;

  const add = (value: string) => {
    const trimmed = value.trim();
    onChange((prev) =>
      prev.some((v) => vocab.normalize(v) === vocab.normalize(trimmed))
        ? prev
        : [...prev, trimmed].slice(0, PICKLIST_SORT_MAX_ORDER_VALUES),
    );
    list.announce(`${trimmed} added to ${fieldLabel} order.`);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="space-y-2">
      <span aria-live="polite" role="status" className="sr-only">
        {list.liveText}
      </span>
      <p id={captionId} className="text-xs text-muted-foreground">
        {note}
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={onDragEnd}
        accessibility={{
          screenReaderInstructions: {
            draggable:
              'Press space bar to pick up this value. Use the arrow keys to move it. Press space bar again to drop it, or escape to cancel. You can also use the up and down buttons on each row.',
          },
        }}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <ol aria-label={`${fieldLabel} order`} aria-describedby={captionId}>
            {values.map((value, index) => (
              <SortableValueRow
                key={ids[index]}
                id={ids[index]!}
                value={value}
                index={index}
                total={values.length}
                fieldLabel={fieldLabel}
                reducedMotion={!!reducedMotion}
                unknown={!!vocab.knownValueKeys && !vocab.knownValueKeys.has(vocab.normalize(value))}
                registerRef={list.registerRef}
                onMove={move}
                onRemove={remove}
              />
            ))}
          </ol>
        </SortableContext>
      </DndContext>

      {atCap ? (
        <p className="text-xs text-muted-foreground">
          You have reached the limit of {PICKLIST_SORT_MAX_ORDER_VALUES} values.
        </p>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={list.addRef}
              variant="outline"
              size="sm"
              role="combobox"
              aria-expanded={open}
              className="h-8 gap-1.5 font-normal"
            >
              <Search aria-hidden="true" />
              Add value
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[260px] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={`Search ${vocab.searchLabel}`}
                value={query}
                onValueChange={setQuery}
              />
              <CommandList>
                {filtered.length === 0 && !queryIsNew ? (
                  <CommandEmpty>No matches. Type to add a new value.</CommandEmpty>
                ) : (
                  <CommandGroup>
                    {filtered.map((option) => (
                      <CommandItem
                        key={option}
                        value={option}
                        onSelect={() => add(option)}
                        className="cursor-pointer"
                      >
                        {option}
                      </CommandItem>
                    ))}
                    {queryIsNew ? (
                      <CommandItem
                        value={query}
                        onSelect={() => add(query)}
                        className="cursor-pointer"
                      >
                        Add &quot;{query.trim()}&quot;
                      </CommandItem>
                    ) : null}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

function SortableValueRow({
  id,
  value,
  index,
  total,
  fieldLabel,
  reducedMotion,
  unknown,
  registerRef,
  onMove,
  onRemove,
}: {
  id: string;
  value: string;
  index: number;
  total: number;
  fieldLabel: string;
  reducedMotion: boolean;
  unknown: boolean;
  registerRef: (key: string) => (el: HTMLButtonElement | null) => void;
  onMove: (index: number, delta: -1 | 1) => void;
  onRemove: (index: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    transition: reducedMotion ? null : undefined,
    attributes: { roleDescription: `sortable ${fieldLabel.toLowerCase()} value` },
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition: transition ?? undefined }}
      className={cn(
        'grid grid-cols-[2rem_1.5rem_minmax(0,1fr)_auto] items-center gap-x-1.5 py-0.5 pr-1',
        isDragging && 'relative z-10 rounded-md bg-background shadow-sm ring-1 ring-border',
      )}
    >
      {/* Handle-only drag: spreading the listeners on the <li> would swallow
          clicks on the chevrons and the remove button. */}
      <button
        type="button"
        className="flex h-8 w-8 cursor-grab touch-none items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:cursor-grabbing"
        aria-label={`Reorder ${value} in ${fieldLabel} order`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" aria-hidden="true" />
      </button>

      <span className="text-xs font-medium tabular-nums text-muted-foreground">{index + 1}.</span>

      <span className="flex min-w-0 flex-wrap items-center gap-1.5">
        <span className="truncate text-sm" title={value}>
          {value}
        </span>
        {unknown ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground">
            <CircleSlash className="h-3 w-3" aria-hidden="true" />
            Not in queue
          </span>
        ) : null}
      </span>

      <span className="flex items-center gap-0.5">
        <EndAwareIconButton
          atEnd={index === 0}
          onClick={() => onMove(index, -1)}
          label={`Move ${value} up in ${fieldLabel} order`}
          icon={ChevronUp}
        />
        <EndAwareIconButton
          atEnd={index === total - 1}
          onClick={() => onMove(index, 1)}
          label={`Move ${value} down in ${fieldLabel} order`}
          icon={ChevronDown}
        />
        <Button
          ref={registerRef(id)}
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onRemove(index)}
          aria-label={`Remove ${value} from ${fieldLabel} order`}
        >
          <X aria-hidden="true" />
        </Button>
      </span>
    </li>
  );
}
